import { FC } from 'react';
import BordSelectStatus from '../../../../../components/NudosComponents/BordSelectStatus/BordSelectStatus';
import { STATUS_VARIANTS } from '../../../../../components/NudosComponents/BordStatus/BordStatus.type';
import { LOGISTIC_SERVICE_STATUS_ID, LOGISTIC_SERVICE_STATUS_NAME } from '../../../../../types/services';
import { capitalize } from 'utils/namingConventionsManagement';

import './LogisticServiceStatusSelect.scss';

/**
 * Props para el componente LogisticServiceStatusSelect
 * @param currentStatus - Estado actual del servicio logístico
 * @param onStatusChange - Función a ejecutar cuando se cambia el estado
 * @param customClassName - Clase CSS personalizada
 */
interface ILogisticServiceStatusSelectProps {
  currentStatus?: LOGISTIC_SERVICE_STATUS_NAME;
  onStatusChange?: (status: LOGISTIC_SERVICE_STATUS_NAME, isLate?: boolean | null) => void;
  customClassName?: string;
  isLate?: boolean | null;
}

const BASE_STATUS_ITEMS = [
  { label: 'Por confirmar', value: LOGISTIC_SERVICE_STATUS_NAME.TOCONFIRMED, variant: STATUS_VARIANTS.default, statusId: 1 },
  { label: 'Confirmado', value: LOGISTIC_SERVICE_STATUS_NAME.CONFIRMED, variant: STATUS_VARIANTS.default, statusId: 2 },
  { label: 'En proceso', value: LOGISTIC_SERVICE_STATUS_NAME.INPROCESS, variant: STATUS_VARIANTS.informative, statusId: 6 },
  { label: 'Activo', value: LOGISTIC_SERVICE_STATUS_NAME.ACTIVE, variant: STATUS_VARIANTS.informative, statusId: 3 },
  { label: 'Entregado', value: LOGISTIC_SERVICE_STATUS_NAME.DELIVERED, variant: STATUS_VARIANTS.success, statusId: 4 },
  { label: 'Cancelado', value: LOGISTIC_SERVICE_STATUS_NAME.CANCELLED, variant: STATUS_VARIANTS.danger, statusId: 5 },
];

const LogisticServiceStatusSelect: FC<ILogisticServiceStatusSelectProps> = ({
  currentStatus = LOGISTIC_SERVICE_STATUS_NAME.TOCONFIRMED,
  onStatusChange,
  customClassName = '',
  isLate
}) => {

  [LOGISTIC_SERVICE_STATUS_NAME.CANCELLED, LOGISTIC_SERVICE_STATUS_NAME.DELIVERED].includes(currentStatus)
  
  const isDropdownDisabled = [LOGISTIC_SERVICE_STATUS_NAME.CANCELLED, LOGISTIC_SERVICE_STATUS_NAME.DELIVERED].includes(currentStatus);

  /**
   * Determina si un estado debe mostrarse y si debe estar habilitado
   * @param itemStatusId - ID del estado a evaluar
   * @param currentStatusId - ID del estado actual
   */
  const getStatusVisibility = (itemStatusId: number, currentStatusId: number) => {
    if (itemStatusId === currentStatusId) {
      return { show: true, enabled: false };
    }
    
    if (itemStatusId === LOGISTIC_SERVICE_STATUS_ID.CANCELLED) {
      return { show: true, enabled: true };
    }
    
    if (itemStatusId < currentStatusId && ![LOGISTIC_SERVICE_STATUS_ID.ACTIVE, LOGISTIC_SERVICE_STATUS_ID.DELIVERED].includes(itemStatusId)) {
      return { show: false, enabled: false };
    }
    let nextValidStatusId;
    switch (currentStatusId) {
      case LOGISTIC_SERVICE_STATUS_ID.TOCONFIRMED: // Por confirmar -> Confirmado
        nextValidStatusId = LOGISTIC_SERVICE_STATUS_ID.CONFIRMED;
        break;
      case LOGISTIC_SERVICE_STATUS_ID.CONFIRMED: // Confirmado -> En proceso
        nextValidStatusId = LOGISTIC_SERVICE_STATUS_ID.INPROCESS;
        break;
      case LOGISTIC_SERVICE_STATUS_ID.INPROCESS: // En proceso -> Activo
        nextValidStatusId = LOGISTIC_SERVICE_STATUS_ID.ACTIVE;
        break;
      case LOGISTIC_SERVICE_STATUS_ID.ACTIVE: // Activo -> Entregado
        return { 
          show: itemStatusId === LOGISTIC_SERVICE_STATUS_ID.DELIVERED, 
          enabled: itemStatusId === LOGISTIC_SERVICE_STATUS_ID.DELIVERED 
        };
      default:
        nextValidStatusId = null;
    }
    
    if (itemStatusId === nextValidStatusId) {
      return { show: true, enabled: true };
    }
    
    return { show: true, enabled: false };
  };

  const availableItems = BASE_STATUS_ITEMS
    .map(item => {
      
      if (!currentStatus) {
        return {
          ...item,
          disabled: item.statusId !== LOGISTIC_SERVICE_STATUS_ID.TOCONFIRMED,
          show: true
        };
      }

      if (currentStatus === LOGISTIC_SERVICE_STATUS_NAME.DELIVERED || 
          currentStatus === LOGISTIC_SERVICE_STATUS_NAME.CANCELLED) {
        return {
          ...item,
          
          disabled: true,
          show: item.value === currentStatus
        };
      }

      const currentItem = BASE_STATUS_ITEMS.find(baseItem => baseItem.value === currentStatus);
      if (!currentItem) return { ...item, disabled: true, show: false };

      const { show, enabled } = getStatusVisibility(item.statusId, currentItem.statusId);
      
      return {
        ...item,
        disabled: !enabled,
        show
      };
    })
    .filter(item => item.show);

  const handleChange = (value: LOGISTIC_SERVICE_STATUS_NAME) => {
    onStatusChange?.(value as LOGISTIC_SERVICE_STATUS_NAME, isLate);
  };
  
    const itemsWithDelay = availableItems.map(item => {
      if (item.value === currentStatus && isLate && item.statusId !== LOGISTIC_SERVICE_STATUS_ID.DELIVERED && item.statusId !== LOGISTIC_SERVICE_STATUS_ID.CANCELLED) {
        return {
          ...item,
          label: capitalize(LOGISTIC_SERVICE_STATUS_NAME.DELAYED) as string,
          variant: STATUS_VARIANTS.danger,
          value: LOGISTIC_SERVICE_STATUS_NAME.DELAYED,
        };
      }
      return item;
    });

  return (
    <BordSelectStatus
      items={itemsWithDelay}
      value={String(currentStatus)}
      onChange={handleChange as (value: string) => void}
      customClassName={customClassName}
      disableDropdown={isDropdownDisabled}
    />
  );
};

export default LogisticServiceStatusSelect;
